/**
 * A span of time, stored as ISO 8601 (`P18M`, `P10Y`) and edited in months.
 *
 * The model keeps the ISO form because that is what `Duration` reads and writes. Nobody types
 * "P18M", though: the drill-down asks for a number of months and turns it into the stored form.
 * Months, because every span the processes ask for — a planning procedure, a ramp-up, a holding
 * period — is naturally counted in them, and a year is twelve of them without loss.
 */

const ISO_MONTHS = /^P(?:(\d+)Y)?(?:(\d+)M)?$/;

/** The months a stored span stands for, or null where it is empty or not in years and months. */
export const monthsOf = (raw: unknown): number | null => {
    if (typeof raw !== 'string' || raw === 'P') return null;

    const match = ISO_MONTHS.exec(raw.trim());
    if (!match || (match[1] === undefined && match[2] === undefined)) return null;

    return Number(match[1] ?? 0) * 12 + Number(match[2] ?? 0);
};

/** The stored form of a number of months; nothing for nothing. */
export const toIsoMonths = (months: number | null | undefined): string | null =>
    months === null || months === undefined || !Number.isFinite(months) ? null : `P${Math.max(0, Math.round(months))}M`;

/** "18 Monate", "2 Jahre" — whole years as years, everything else as months. */
export const formatDuration = (raw: unknown, lang: 'de' | 'en'): string | null => {
    const months = monthsOf(raw);
    if (months === null) return null;

    if (months >= 12 && months % 12 === 0) {
        const years = months / 12;
        if (lang === 'en') return `${years} ${years === 1 ? 'year' : 'years'}`;
        return `${years} ${years === 1 ? 'Jahr' : 'Jahre'}`;
    }

    if (lang === 'en') return `${months} ${months === 1 ? 'month' : 'months'}`;
    return `${months} ${months === 1 ? 'Monat' : 'Monate'}`;
};
